// web-v2/src/components/sheets/BindHoldSheet.jsx
//
// A PILOT CHANGE SINCE THE QUOTE, AS DAVID REVIEWS IT (decision 8). Information
// only (ruling 2026-10-19: the client is never blocked; nothing waits on David):
// release records that the quote stands, re-quote records a note, and neither
// gates the client.
//
// Reads GET /api/tracon/bind-holds?deal= (services/bind-holds readHoldStates,
// the one composed reader) and prints the server's words only: per held pilot
// the heading, the title when the payload carries it (holdTitle wording), the comparison sentence, and ONLY the open items, each with what
// the form said when the quote was presented and what it says now. This file
// compares nothing, formats no date and words no change: an item's kind picks
// a layout, never a sentence about the pilot.
//
//   unmeasured   nothing could be compared, so no "What changed" table and no
//                change claimed; the same two buttons
//   read failed  "Could not check", and no release (an empty result and a
//                failed call are not the same thing)
//   unknown      a row that says neither held nor resolved, or holds signing
//                with no open item: "Could not check this hold", no release
//
// Release posts exactly the open item keys on screen; the server refuses a set
// that moved (HOLD_CHANGED). After either decision the sheet RE-READS the GET
// and the toast says what that read shows (a confirmation is a read, not an
// echo); server warnings toast.info each. No native dialogs: the sheet is the
// review, so there is no second confirm.
import { useCallback, useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { getBindHolds, releaseBindHold, requoteBindHold } from '@/services/bind-holds.service.js';

export const COPY = Object.freeze({
  // Neutral: the sheet also opens on a deal with nothing held (the deal verb).
  title: 'Pilot changes since the quote',
  description: 'For your information. The client can still sign, and the bind request lists these changes. Release records that the quote stands; re-quote records a note. Neither blocks the client.',
  empty: 'No pilot has changed since the quote on this deal.',
  readFailed: 'Could not check pilot changes on this deal. Try again in a moment.',
  checking: 'Checking pilot changes on this deal...',
  unknown: 'Could not check this change.',
  unmeasured: 'Nothing could be compared, so no change is claimed.',
  release: 'Release (quote stands)',
  releasing: 'Releasing...',
  requote: 'Re-quote',
  saving: 'Saving...',
  retry: 'Check again',
});

export const holdKey = (h) => `${h.quote_id}:${h.pilot_key}`;
// Which of the server's answers this row is. Not a state rule: it reads the
// server's holds_signing and refuses a contradictory row (held with no open
// item, the queue's own re-check in utils/queue-derive), so a release can never
// post an empty or unread item list.
export function holdAnswer(h) {
  if (h && h.holds_signing === true && Array.isArray(h.open_items) && h.open_items.length) return 'held';
  if (h && h.holds_signing === false) return 'resolved';
  return 'unknown';
}

function ItemRows({ items }) {
  return (
    <table className="w-full border-collapse text-left text-[12.5px]">
      <thead>
        <tr className="border-b border-border text-text-muted">
          <th className="py-1.5 pr-3 font-medium">What changed</th>
          <th className="py-1.5 pr-3 font-medium">When presented</th>
          <th className="py-1.5 font-medium">Now</th>
        </tr>
      </thead>
      <tbody>
        {items.map((i) => (
          <tr key={i.key} className="border-b border-border/60 align-top">
            <td className="py-1.5 pr-3 text-text">
              {i.label}
              {(i.details || []).map((d, n) => (
                <div key={n} className="text-[11.5px] text-text-muted">{d.label}: {d.value_text}</div>
              ))}
            </td>
            <td className="py-1.5 pr-3 text-text-muted">{i.was_text}</td>
            <td className="py-1.5 text-text">{i.now_text}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Released({ hold }) {
  if (!hold.released_heading) return null;
  return (
    <details className="text-[12px] text-text-muted">
      <summary className="cursor-pointer select-none">{hold.released_heading}</summary>
      <ul className="mt-1 space-y-0.5 pl-4">
        {(hold.released_rows || []).map((r, n) => <li key={n}>{r}</li>)}
      </ul>
    </details>
  );
}

function HeldCard({ hold, busy, onRelease, onRequote }) {
  const items = hold.open_items || [];
  const changes = items.filter((i) => i.kind !== 'unmeasured');
  const unmeasured = items.filter((i) => i.kind === 'unmeasured');
  const k = holdKey(hold);
  const mine = busy && busy.key === k ? busy.action : null;
  return (
    <section data-hold={k} className="space-y-2.5 rounded-card border border-border bg-surface px-3.5 py-3">
      <div>
        <h3 className="text-[14px] font-semibold text-text">{hold.heading}</h3>
        {hold.title ? <p className="text-[12.5px] text-text">{hold.title}</p> : null}
        {hold.sub ? <p className="text-[12px] text-text-muted">{hold.sub}</p> : null}
      </div>
      {changes.length ? <ItemRows items={changes} /> : null}
      {unmeasured.map((i) => (
        <div key={i.key} className="text-[12.5px]">
          <p className="text-text">{i.summary}</p>
          <p className="text-text-muted">{COPY.unmeasured}</p>
        </div>
      ))}
      {hold.requote_text ? <p className="text-[12px] text-text-muted">{hold.requote_text}</p> : null}
      <Released hold={hold} />
      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="button"
          disabled={!!busy}
          onClick={() => onRelease?.(hold)}
          className="inline-flex min-h-[36px] items-center rounded-[5px] border border-accent bg-accent px-3 py-1.5 text-[12.5px] text-white hover:brightness-110 disabled:opacity-50"
        >
          {mine === 'release' ? COPY.releasing : COPY.release}
        </button>
        <button
          type="button"
          disabled={!!busy}
          onClick={() => onRequote?.(hold)}
          className="inline-flex min-h-[36px] items-center rounded-[5px] border border-border bg-surface px-3 py-1.5 text-[12.5px] text-text hover:border-border-strong disabled:opacity-50"
        >
          {mine === 'requote' ? COPY.saving : COPY.requote}
        </button>
      </div>
    </section>
  );
}

/**
 * Presentational: prints the payload, compares nothing.
 * holds: the GET's list, or null while reading. readFailed: the read failed.
 * busy: { key, action } while a decision is in flight.
 */
export function BindHoldCards({ holds, busy = null, onRelease, onRequote, readFailed = false }) {
  if (readFailed) return <p role="alert" className="text-[13px] text-text">{COPY.readFailed}</p>;
  if (!Array.isArray(holds)) return <p className="text-[13px] text-text-muted">{COPY.checking}</p>;
  const held = holds.filter((h) => holdAnswer(h) === 'held');
  const resolved = holds.filter((h) => holdAnswer(h) === 'resolved');
  // Neither answer, or a contradictory one, is not an answer: say so, offer nothing.
  const unknown = holds.filter((h) => holdAnswer(h) === 'unknown');
  return (
    <div className="space-y-3">
      {held.length === 0 && unknown.length === 0 ? <p className="text-[13px] text-text-muted">{COPY.empty}</p> : null}
      {held.map((h) => <HeldCard key={holdKey(h)} hold={h} busy={busy} onRelease={onRelease} onRequote={onRequote} />)}
      {unknown.map((h, n) => (
        <p key={`unknown-${n}`} role="alert" className="text-[12.5px] text-text">{h && h.heading ? `${h.heading}: ` : ''}{COPY.unknown}</p>
      ))}
      {resolved.filter((h) => h.released_heading).map((h) => (
        <div key={holdKey(h)} className="space-y-1 rounded-card border border-border/60 px-3.5 py-2">
          <p className="text-[12.5px] font-medium text-text-muted">{h.heading}</p>
          <Released hold={h} />
        </div>
      ))}
    </div>
  );
}

const COULD_NOT_CONFIRM = 'Saved. Could not check the result; reopen to confirm.';
// What the re-read shows, as one toast. fresh: the GET's list, or null.
function reportOutcome(action, name, fresh, k, res) {
  for (const w of Array.isArray(res?.warnings) ? res.warnings : []) toast.info(String(w));
  if (!Array.isArray(fresh)) { toast.info(COULD_NOT_CONFIRM); return; }
  const now = fresh.find((h) => h && holdKey(h) === k) || null;
  const said = now ? holdAnswer(now) : null;
  // A re-read that cannot be read as held or resolved is not a result.
  if (said === 'unknown') { toast.info(COULD_NOT_CONFIRM); return; }
  if (action === 'release') {
    if (!now || now.holds_signing === false) toast.success(`Released. The quote stands for ${name}. Nothing was sent to the client.`);
    else toast.info(`Saved, but ${name}'s form has another change open. Review the updated list.`);
    return;
  }
  if (now && now.state === 'requoting') {
    toast.success(`${res?.unchanged ? 'Already marked' : 'Marked'} for a new quote for ${name}. Nothing was sent to the client.`);
  } else toast.info(`Saved, but ${name}'s hold changed. Review the updated list.`);
}

function reportRefusal(err, name) {
  const code = err?.body?.code;
  if (code === 'HOLD_CHANGED') toast.info(`${name}'s form changed again while you were looking. Review the updated list.`);
  else if (code === 'HOLD_MOOT') toast.info(err.body.error || 'This hold is no longer open.');
  else toast.error(err?.body?.error || 'Could not record your decision. Nothing was released.');
}

export default function BindHoldSheet({ dealId, open, onOpenChange, onChange }) {
  const [holds, setHolds] = useState(null);
  const [readFailed, setReadFailed] = useState(false);
  const [busy, setBusy] = useState(null);

  // Returns the list read, or null when the read failed (never [] for a failure).
  const load = useCallback(async () => {
    try {
      const r = await getBindHolds(dealId);
      if (!r || !Array.isArray(r.holds)) throw new Error('the hold read returned no list');
      setHolds(r.holds); setReadFailed(false);
      return r.holds;
    } catch (_) {
      setHolds(null); setReadFailed(true);
      return null;
    }
  }, [dealId]);

  useEffect(() => {
    if (!open || !dealId) return;
    setHolds(null); setReadFailed(false);
    load();
  }, [open, dealId, load]);

  async function decide(hold, action) {
    const k = holdKey(hold);
    const name = hold.heading;
    setBusy({ key: k, action });
    const body = { deal_id: String(dealId), quote_id: hold.quote_id, pilot_key: hold.pilot_key, item_keys: (hold.open_items || []).map((i) => i.key) };
    let res = null; let err = null;
    try { res = await (action === 'release' ? releaseBindHold : requoteBindHold)(body); }
    catch (e) { err = e; }
    const fresh = await load();
    setBusy(null);
    if (typeof onChange === 'function') onChange();
    if (err) reportRefusal(err, name);
    else reportOutcome(action, name, fresh, k, res);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>{COPY.title}</SheetTitle>
          <SheetDescription>{COPY.description}</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-1 py-3">
          <BindHoldCards
            holds={holds}
            busy={busy}
            readFailed={readFailed}
            onRelease={(h) => decide(h, 'release')}
            onRequote={(h) => decide(h, 'requote')}
          />
          {readFailed ? (
            <button
              type="button"
              onClick={() => { setReadFailed(false); load(); }}
              className="mt-3 inline-flex min-h-[36px] items-center rounded-[5px] border border-border bg-surface px-3 py-1.5 text-[12.5px] text-text hover:border-border-strong"
            >
              {COPY.retry}
            </button>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
